/**
 * Statistik-, Regressions- und Clustering-Hilfen für die Auswertung
 */

/**
 * Berechnet Min, Max, Mittelwert, Std, Varianz, Spannweite, Variationskoeffizient
 * @param {number[]} arr - Werte (z.B. individualTime in Hundertstel-Minuten)
 * @returns {{ n, min, max, mean, std, var, spanne, varKoeff }}
 */
export function computeStats(arr) {
  if (!arr || arr.length === 0) {
    return { n: 0, min: 0, max: 0, mean: 0, std: 0, var: 0, spanne: 0, varKoeff: null };
  }
  const n = arr.length;
  const min = Math.min(...arr);
  const max = Math.max(...arr);
  const sum = arr.reduce((a, b) => a + b, 0);
  const mean = sum / n;
  const variance = arr.reduce((a, v) => a + (v - mean) ** 2, 0) / n;
  const std = Math.sqrt(variance);
  const spanne = max - min;
  const varKoeff = mean !== 0 ? (std / mean) * 100 : null;
  return { n, min, max, mean, std, var: variance, spanne, varKoeff };
}

/**
 * Einfache lineare Regression: y = slope * x + intercept
 * @param {number[]} x
 * @param {number[]} y
 * @returns {{ slope, intercept, r2 }}
 */
export function simpleOLS(x, y) {
  const n = x.length;
  if (n < 2) return { slope: 0, intercept: 0, r2: 0 };
  const mx = x.reduce((a, b) => a + b, 0) / n;
  const my = y.reduce((a, b) => a + b, 0) / n;
  let cov = 0, vx = 0;
  for (let i = 0; i < n; i++) {
    cov += (x[i] - mx) * (y[i] - my);
    vx += (x[i] - mx) ** 2;
  }
  const slope = vx !== 0 ? cov / vx : 0;
  const intercept = my - slope * mx;
  let ssTot = 0, ssRes = 0;
  for (let i = 0; i < n; i++) {
    ssTot += (y[i] - my) ** 2;
    const pred = slope * x[i] + intercept;
    ssRes += (y[i] - pred) ** 2;
  }
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 0;
  return { slope, intercept, r2 };
}

/**
 * Einfache lineare Regression inkl. Parameter für Trompetenkurve (Prediction-Band).
 * @param {number[]} x
 * @param {number[]} y
 * @returns {{ slope, intercept, r2, s, Sxx, xMean, n }} s = Residuen-Standardfehler, Sxx = sum (x_i - x̄)²
 */
export function simpleOLSWithBand(x, y) {
  const n = x.length;
  if (n < 3) {
    const mx = n ? x.reduce((a, b) => a + b, 0) / n : 0;
    const Sxx = n ? x.reduce((a, v) => a + (v - mx) ** 2, 0) : 0;
    return { slope: 0, intercept: n ? y.reduce((a, b) => a + b, 0) / n : 0, r2: 0, s: 0, Sxx, xMean: mx, n };
  }
  const o = simpleOLS(x, y);
  const mx = x.reduce((a, b) => a + b, 0) / n;
  const Sxx = x.reduce((a, v) => a + (v - mx) ** 2, 0);
  let ssRes = 0;
  for (let i = 0; i < n; i++) {
    const pred = o.slope * x[i] + o.intercept;
    ssRes += (y[i] - pred) ** 2;
  }
  const s = Math.sqrt(ssRes / (n - 2));
  return { ...o, s, Sxx, xMean: mx, n };
}

/**
 * Multivariate lineare Regression: y = X * beta (X enthält Spalte 1 für Intercept)
 * @param {number[][]} X - Zeilen [1, x1, x2, ...]
 * @param {number[]} y
 * @returns {{ beta: number[], r2: number }}
 */
export function multilinearOLS(X, y) {
  const n = X.length;
  const p = X[0]?.length || 0;
  if (n < p || p === 0) return { beta: Array(p).fill(0), r2: 0 };

  // X'X
  const XtX = [];
  for (let i = 0; i < p; i++) {
    XtX[i] = [];
    for (let j = 0; j < p; j++) {
      let s = 0;
      for (let k = 0; k < n; k++) s += X[k][i] * X[k][j];
      XtX[i][j] = s;
    }
  }
  // X'y
  const Xty = [];
  for (let i = 0; i < p; i++) {
    let s = 0;
    for (let k = 0; k < n; k++) s += X[k][i] * y[k];
    Xty[i] = s;
  }
  const inv = invertMatrix(XtX);
  if (!inv) return { beta: Array(p).fill(0), r2: 0 };
  const beta = [];
  for (let i = 0; i < p; i++) {
    let s = 0;
    for (let j = 0; j < p; j++) s += inv[i][j] * Xty[j];
    beta[i] = s;
  }
  const my = y.reduce((a, b) => a + b, 0) / n;
  let ssTot = 0, ssRes = 0;
  for (let i = 0; i < n; i++) {
    ssTot += (y[i] - my) ** 2;
    let pred = 0;
    for (let j = 0; j < p; j++) pred += X[i][j] * beta[j];
    ssRes += (y[i] - pred) ** 2;
  }
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 0;
  return { beta, r2 };
}

function invertMatrix(A) {
  const n = A.length;
  if (n === 0 || A[0]?.length !== n) return null;
  if (n === 2) {
    const [[a, b], [c, d]] = A;
    const det = a * d - b * c;
    if (Math.abs(det) < 1e-10) return null;
    return [[d / det, -b / det], [-c / det, a / det]];
  }
  if (n === 3) {
    const [[a, b, c], [d, e, f], [g, h, i]] = A;
    const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
    if (Math.abs(det) < 1e-10) return null;
    return [
      [(e * i - f * h) / det, (c * h - b * i) / det, (b * f - c * e) / det],
      [(f * g - d * i) / det, (a * i - c * g) / det, (c * d - a * f) / det],
      [(d * h - e * g) / det, (b * g - a * h) / det, (a * e - b * d) / det]
    ];
  }
  // Gauss-Jordan für n×n (n>3): [A|I] -> [I|A^-1]
  const M = A.map((row, i) => {
    const right = new Array(n).fill(0);
    right[i] = 1;
    return [...row, ...right];
  });
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    }
    [M[col], M[pivot]] = [M[pivot], M[col]];
    if (Math.abs(M[col][col]) < 1e-10) return null;
    const d = M[col][col];
    for (let j = 0; j < 2 * n; j++) M[col][j] /= d;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col];
      for (let j = 0; j < 2 * n; j++) M[r][j] -= f * M[col][j];
    }
  }
  return M.map(row => row.slice(n));
}

/**
 * R² für kategoriale Gruppierung: R² = SS_between / SS_total
 * @param {number[]} y - metrische Variable (z.B. vorgabeTime)
 * @param {string[]} groups - Gruppenzuordnung (gleiche Länge wie y)
 * @returns {number} R² in [0,1] oder 0
 */
export function r2Categorical(y, groups) {
  const n = y.length;
  if (n < 2 || !groups || groups.length !== n) return 0;
  const yMean = y.reduce((a, b) => a + b, 0) / n;
  let ssTot = 0;
  const gm = {}, gc = {};
  for (let i = 0; i < n; i++) {
    ssTot += (y[i] - yMean) ** 2;
    const g = groups[i] == null ? '—' : String(groups[i]);
    gm[g] = (gm[g] || 0) + y[i];
    gc[g] = (gc[g] || 0) + 1;
  }
  Object.keys(gm).forEach(g => { gm[g] /= gc[g]; });
  let ssBetween = 0;
  Object.keys(gm).forEach(g => { ssBetween += gc[g] * (gm[g] - yMean) ** 2; });
  return ssTot > 0 ? ssBetween / ssTot : 0;
}

/**
 * Ermittelt welche Einflussgrößen die Sollzeit (vorgabeTime) erklären.
 * Für jede EG: R² = r2Categorical(vorgabeTime, Ausprägung).
 * @param {object[]} measurements - mit vorgabeTime, einflussgroessen {[name]: ausprägung}
 * @param {number} r2Min - Mindest-R² zur Aufnahme (default 0.01)
 * @param {number} maxEG - max. Anzahl zeitbestimmender EGs (default 10)
 * @returns {{ allEG: string[], zeitbestimmend: string[], r2PerEG: Record<string,number> }}
 */
export function analyzeEinflussgroessenZeitbestimmend(measurements, r2Min = 0.01, maxEG = 10) {
  const allEG = [...new Set(measurements.flatMap(m => Object.keys(m.einflussgroessen || {})))];
  const r2PerEG = {};
  allEG.forEach(eg => {
    const sub = measurements.filter(m => m.einflussgroessen && (m.einflussgroessen[eg] != null && m.einflussgroessen[eg] !== ''));
    const y = sub.map(m => m.vorgabeTime != null ? Number(m.vorgabeTime) : 0);
    const groups = sub.map(m => String(m.einflussgroessen[eg]));
    if (y.length >= 2 && [...new Set(groups)].length >= 2) {
      r2PerEG[eg] = r2Categorical(y, groups);
    } else {
      r2PerEG[eg] = 0;
    }
  });
  const zeitbestimmend = allEG
    .filter(eg => (r2PerEG[eg] || 0) >= r2Min)
    .sort((a, b) => (r2PerEG[b] || 0) - (r2PerEG[a] || 0))
    .slice(0, maxEG);
  return { allEG, zeitbestimmend, r2PerEG };
}

/**
 * Designmatrix X (One-Hot für kategoriale EGs) und y = vorgabeTime.
 * Pro EG: Referenz = erste Ausprägung, weitere als 0/1-Dummies.
 * @param {object[]} measurements - mit vorgabeTime, einflussgroessen
 * @param {string[]} zeitbestimmendEG - EGs die einfließen
 * @returns {{ X: number[][], y: number[], colNames: string[] }} colNames[0]=Intercept, danach EG_Ausprägung
 */
export function buildDesignMatrixEA(measurements, zeitbestimmendEG) {
  if (!zeitbestimmendEG || zeitbestimmendEG.length === 0) {
    const y = measurements.map(m => m.vorgabeTime != null ? Number(m.vorgabeTime) : 0);
    return { X: y.map(() => [1]), y, colNames: ['Intercept'] };
  }
  // Pro EG: Levels sammeln (Ausprägungen inkl. "—" bei fehlend)
  const levels = {};
  zeitbestimmendEG.forEach(eg => {
    const L = new Set();
    measurements.forEach(m => {
      const v = m.einflussgroessen?.[eg];
      L.add(v != null && v !== '' ? String(v) : '—');
    });
    levels[eg] = [...L].sort();
  });
  const colNames = ['Intercept'];
  zeitbestimmendEG.forEach(eg => {
    const L = levels[eg];
    for (let i = 1; i < L.length; i++) colNames.push(eg + ':' + L[i]);
  });
  const X = [];
  const y = [];
  measurements.forEach(m => {
    const row = [1];
    zeitbestimmendEG.forEach(eg => {
      const L = levels[eg];
      const v = m.einflussgroessen?.[eg];
      const val = v != null && v !== '' ? String(v) : '—';
      for (let i = 1; i < L.length; i++) row.push(L[i] === val ? 1 : 0);
    });
    const yVal = m.vorgabeTime != null ? Number(m.vorgabeTime) : 0;
    X.push(row);
    y.push(yVal);
  });
  return { X, y, colNames };
}

/**
 * K-Means-Clustering (Euklidisch, Normalisierung auf [0,1] pro Dimension)
 * @param {number[][]} points - [[f1,f2,...], ...]
 * @param {number} k - Anzahl Cluster
 * @param {number} maxIter
 * @returns {number[]} - Cluster-Index pro Punkt
 */
export function kMeans(points, k, maxIter = 20) {
  const n = points.length;
  if (n === 0 || k <= 0 || k > n) return [];
  const dim = points[0].length;
  const mins = [], ranges = [];
  for (let j = 0; j < dim; j++) {
    let mn = points[0][j], mx = points[0][j];
    for (let i = 1; i < n; i++) {
      mn = Math.min(mn, points[i][j]);
      mx = Math.max(mx, points[i][j]);
    }
    mins[j] = mn;
    ranges[j] = mx === mn ? 1 : mx - mn;
  }
  const P = points.map(pp => pp.map((v, j) => (v - mins[j]) / ranges[j]));

  let assign = P.map(() => Math.floor(Math.random() * k));
  for (let iter = 0; iter < maxIter; iter++) {
    const cents = [];
    for (let c = 0; c < k; c++) {
      const group = P.filter((_, i) => assign[i] === c);
      if (group.length === 0) {
        cents[c] = P[Math.floor(Math.random() * n)].slice();
      } else {
        const cent = [];
        for (let j = 0; j < dim; j++) cent[j] = group.reduce((a, p) => a + p[j], 0) / group.length;
        cents[c] = cent;
      }
    }
    let ch = 0;
    for (let i = 0; i < n; i++) {
      let best = 0, bestD = 1e30;
      for (let c = 0; c < k; c++) {
        let d = 0;
        for (let j = 0; j < dim; j++) d += (P[i][j] - cents[c][j]) ** 2;
        if (d < bestD) { bestD = d; best = c; }
      }
      if (assign[i] !== best) { ch++; assign[i] = best; }
    }
    if (ch === 0) break;
  }
  return assign;
}
